import {
  type DocumentMeta,
  type DocumentStatus,
  documentStatus,
  useSelectionManager
} from "@prismicio/editor-fields"
import { range } from "@prismicio/editor-support/Array"
import { getDateDiffString } from "@prismicio/editor-support/Date"
import { useRequest } from "@prismicio/editor-support/Suspense"
import {
  Avatar,
  Badge,
  Box,
  Checkbox,
  ErrorBoundary,
  Icon,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Text,
  useToast
} from "@prismicio/editor-ui"
import { type MouseEvent, type TransitionStartFunction } from "react"
import { Link } from "react-router-dom"

import { getCustomType } from "../../app/services/customTypes"
import { useBulkAction } from "../BulkActions/BulkActionsProvider"
import { isStandardTaskRecord } from "../BulkActions/bulkActionsService"
import { useSearchDocumentParams } from "../useDocumentList"
import {
  CreateNewDocumentButton,
  type CreateNewDocumentProps
} from "./CreateNewDocumentDialog"
import { useDocumentDataSearch, usePageBottom } from "./useDocumentData"

interface DocumentListTableProps {
  tab: "working" | "archived" | "upcoming"
  /**
   * The release id to filter the documents by.
   * If not provided, the documents will be filtered by the current release.
   * Only works for the "upcoming" tab.
   */
  releaseId?: string
  startTransition: TransitionStartFunction
  setModalOpen: (isOpen: boolean) => void
}

export function DocumentListTable(props: DocumentListTableProps) {
  const { startTransition, tab, setModalOpen, releaseId } = props

  const { showToast } = useToast()

  const { documents, hasNextPage, fetchNextPage, refresh } =
    useDocumentDataSearch({
      onCacheInvalidated: () =>
        showToast({
          title: "There are new search results to display here.",
          action: {
            title: "Update",
            onClick: refresh
          },
          icon: "alert",
          seconds: 10
        }),
      tab,
      releaseId
    })

  const selectionManager = useSelectionManager({
    totalItemCount: documents.length
  })

  function onSelectItem(id: string, event: MouseEvent | KeyboardEvent) {
    const ids = event.shiftKey ? documents.map(({ id }) => id) : undefined

    selectionManager.toggleSelection({
      id,
      metaKey: event.metaKey,
      shiftKey: event.shiftKey,
      ids
    })
  }

  usePageBottom(() => hasNextPage && startTransition(fetchNextPage))

  const { isLoadingDocuments } = useSearchDocumentParams()

  if (documents.length === 0 && tab === "working")
    return <DocumentListEmptyBody setModalOpen={setModalOpen} />

  // TODO: Add the empty table design case, Ticket PBD-998
  if (documents.length === 0 && tab === "archived") return null

  return (
    <Table isLoading={isLoadingDocuments} columnLayout={columnLayout}>
      <DocumentListTableHeader />
      <TableBody>
        {documents.map((document) => (
          <DocumentListTableRow
            document={document}
            onSelectCheckbox={(_, event) => onSelectItem(document.id, event)}
            isChecked={selectionManager.isSelected(document.id)}
            // Add status to key because of an API bug where the document id can be returned twice PBD-1082
            key={`${document.id}-${document.versions[0]?.status ?? ""}`}
          />
        ))}
      </TableBody>
    </Table>
  )
}

interface DocumentListTableHeaderProps {
  checkbox?: boolean
}

export function DocumentListTableHeader(props: DocumentListTableHeaderProps) {
  const { checkbox = true } = props

  return (
    <TableHead>
      <TableRow>
        {checkbox && <TableCell />}
        <DocumentListTableHeaderCell text="Status" />
        <DocumentListTableHeaderCell text="Name" />
        <DocumentListTableHeaderCell text="Type" />
        <DocumentListTableHeaderCell text="Last update" />
        <DocumentListTableHeaderCell text="Author" align="end" />
      </TableRow>
    </TableHead>
  )
}

interface DocumentListTableHeaderCellProps {
  text: string
  align?: "end"
}

function DocumentListTableHeaderCell(props: DocumentListTableHeaderCellProps) {
  const { text, align } = props

  return (
    <TableCell align={align}>
      <Text variant="smallBold" color="grey11" noSelect noWrap>
        {text}
      </Text>
    </TableCell>
  )
}

interface DocumentListTableRowProps {
  document: DocumentMeta
  checkbox?: boolean
  isChecked?: boolean
  onSelectCheckbox?: (checked: boolean, event: MouseEvent) => void
}

export function DocumentListTableRow(props: DocumentListTableRowProps) {
  const {
    document,
    checkbox = true,
    isChecked = false,
    onSelectCheckbox
  } = props
  const { id, title, custom_type_id: customTypeId, versions } = document

  const { isLoadingDocuments } = useSearchDocumentParams()

  const lastEdited = versions[0]
    ? getDateDiffString(versions[0].last_modified_date)
    : ""

  const firstName = versions[0]?.author.first_name ?? ""
  const lastName = versions[0]?.author.last_name ?? ""

  const status = versions[0]?.status ?? "unclassified"

  const { documentsInQueue } = useBulkAction()
  const inProgressInBulkAction = documentsInQueue.some((_document) =>
    isStandardTaskRecord(_document)
      ? _document.documentId === document.id
      : _document.documentGroupLangId === document.group_lang_id
  )

  function onCheckedChange(checked: boolean, event: MouseEvent) {
    if (inProgressInBulkAction) return

    onSelectCheckbox?.(checked, event)
  }

  return (
    <TableRow
      disabled={inProgressInBulkAction}
      renderLink={(children) => (
        <Link to={`/pages/${id}?s=${status}`}>{children}</Link>
      )}
    >
      {checkbox && (
        <TableCell interactive={!isLoadingDocuments}>
          <Checkbox
            checked={isChecked}
            tabIndex={-1}
            disabled={isLoadingDocuments}
            onCheckedChange={onCheckedChange}
          />
        </TableCell>
      )}
      <TableCell>
        <StatusBadge status={status} />
      </TableCell>
      <TableCell>
        <Text variant="bold" color="grey12" noWrap>
          {title}
        </Text>
      </TableCell>
      <TableCell>
        <ErrorBoundary renderError={() => id}>
          <CustomTypeName id={customTypeId} />
        </ErrorBoundary>
      </TableCell>
      <TableCell>
        <Text variant="small" color="grey11" noWrap>
          {lastEdited}
        </Text>
      </TableCell>
      <TableCell align="end">
        <Avatar firstName={firstName} lastName={lastName} />
      </TableCell>
    </TableRow>
  )
}

interface CustomTypeNameProps {
  id: string
}

function CustomTypeName(props: CustomTypeNameProps) {
  const customType = useCustomType(props)

  return (
    <Text variant="small" color="grey11" noWrap>
      {customType.label}
    </Text>
  )
}

function useCustomType(params: CustomTypeNameProps) {
  return useRequest(getCustomType, [params.id])
}

interface StatusBadgeProps {
  status: DocumentStatus
}

function StatusBadge(props: StatusBadgeProps) {
  const { status } = props

  const { title, color } = documentStatus[status]

  return <Badge title={title} color={color} size="medium" maxWidth={150} />
}

interface DocumentListLoadingTableProps {
  checkbox?: boolean
  numberOfRows?: number
  border?: boolean
  radius?: boolean
  columns?: string
}

export function DocumentListLoadingTable(props: DocumentListLoadingTableProps) {
  const {
    checkbox = true,
    numberOfRows = 10,
    border = true,
    radius = true,
    columns = columnLayout
  } = props

  return (
    <Table
      columnLayout={columns}
      isInteractive={false}
      border={border}
      radius={radius}
    >
      <DocumentListTableHeader checkbox={checkbox} />
      <TableBody>
        {range(0, numberOfRows - 1).map((num) => (
          <TableLoadingRow key={num} checkbox={checkbox} />
        ))}
      </TableBody>
    </Table>
  )
}

interface TableLoadingRowProps {
  checkbox?: boolean
}

function TableLoadingRow(props: TableLoadingRowProps) {
  const { checkbox = true } = props

  return (
    <TableRow>
      {checkbox && (
        <TableCell>
          <Checkbox tabIndex={-1} disabled />
        </TableCell>
      )}
      <TableCell>
        <Skeleton width={40} height={24} />
      </TableCell>
      <TableCell>
        <Skeleton width={371} height={16} />
      </TableCell>
      <TableCell>
        <Skeleton width={56} height={16} />
      </TableCell>
      <TableCell>
        <Skeleton width={56} height={16} />
      </TableCell>
      <TableCell align="end">
        <Skeleton width={32} height={32} borderRadius="100%" />
      </TableCell>
    </TableRow>
  )
}

interface DocumentListEmptyBodyProps extends CreateNewDocumentProps {}

function DocumentListEmptyBody(props: DocumentListEmptyBodyProps) {
  const { setModalOpen } = props

  const { isLoadingDocuments } = useSearchDocumentParams()

  return (
    <Box
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      flexGrow={1}
      padding={{ top: 100 }}
      gap={16}
      opacity={isLoadingDocuments ? 0.3 : 1}
    >
      <Box borderRadius="100%" padding={16} backgroundColor="purple5">
        <Icon size="large" name="autoFixHigh" color="purple11" />
      </Box>

      <Text variant="h2">Once Upon a Time...</Text>

      <Text variant="normal" color="grey11">
        This is the place where your stories take life
      </Text>

      <CreateNewDocumentButton setModalOpen={setModalOpen} />
    </Box>
  )
}

export const columnLayout = `
  30px
  ${minWidthColumn("2fr")}
  ${minWidthColumn("13fr")}
  ${minWidthColumn("4fr")}
  ${minWidthColumn("3fr")}
  ${minWidthColumn("1fr")}
`

export const columnLayoutWithoutCheckbox = `
  ${minWidthColumn("2fr")}
  ${minWidthColumn("13fr")}
  ${minWidthColumn("4fr")}
  ${minWidthColumn("3fr")}
  ${minWidthColumn("1fr")}
`

// For the more important content columns, this minimum width
// gives them a chance to look decent on smaller screens and/or show an ellipsis.
function minWidthColumn(max: string) {
  return `minmax(60px, ${max})`
}
